'use client'

import React, { useState, useLayoutEffect, useEffect, useRef } from 'react'
import {
  ChevronDown,
  ArrowRight,
  Menu,
  X,
  Briefcase,
  Sparkles,
  Building2,
  Rocket,
  GraduationCap,
  Heart,
  TrendingUp,
  BookOpen,
  FileText,
  Shield,
  Users,
  Layers,
  Lightbulb,
  Map,
  CreditCard,
  MessageCircle,
  Calculator,
  PlayCircle,
  CalendarDays,
} from 'lucide-react'
import Link from 'next/link'
import Image from 'next/image'
import { motion, AnimatePresence } from 'framer-motion'
import { getMediaUrl, mediaFile } from '../../lib/media'
import type { FeaturedResource } from './types'
import { trackCtaClicked } from '../../lib/analytics'
import { getPersonaHref } from '@/lib/persona-paths'

// Persona data for the "Who We Help" dropdown
const personas = [
  {
    slug: 'founders',
    name: 'Startup Founders',
    headline: 'Immigration as accelerant',
    description: 'Turning immigration into a competitive advantage for startups.',
    visas: 'O-1 / E-2 / EB-1 / EB-2 NIW',
    icon: Rocket,
  },
  {
    slug: 'professionals',
    name: 'Professionals',
    headline: 'Moving professionals forward',
    description: 'Strategic immigration expertise for building careers in the U.S.',
    visas: 'TN / H-1B / PERM / EB-2',
    icon: Briefcase,
  },
  {
    slug: 'extraordinary-individuals',
    name: 'Extraordinary Individuals',
    headline: 'Bespoke solutions for extraordinary talent',
    description: "You've achieved exceptional things. We make sure the U.S. recognizes it.",
    visas: 'O-1 / EB-1',
    icon: Sparkles,
  },
  {
    slug: 'employers',
    name: 'Employers & HR Teams',
    headline: 'Effortless immigration management',
    description: 'Recruit, hire and retain global talent, painlessly.',
    visas: 'All Work Visas',
    icon: Building2,
  },
  {
    slug: 'students',
    name: 'Students & Recent Grads',
    headline: 'A clearer pathway to stay',
    description: 'Navigate the transition from school to long-term opportunities.',
    visas: 'OPT / H-1B / O-1',
    icon: GraduationCap,
  },
  {
    slug: 'families',
    name: 'Families',
    headline: 'Build your lives here together',
    description: 'Personalized solutions for families building a life in the U.S.',
    visas: 'Family-Based Green Cards',
    icon: Heart,
  },
  {
    slug: 'investors',
    name: 'Investors',
    headline: 'Immigration as business strategy',
    description: 'Aligning immigration planning with capital and long-term objectives.',
    visas: 'EB-5 / E-2',
    icon: TrendingUp,
  },
]

const navItems = [
  {
    label: 'Your Team',
    href: '/about',
  },
  {
    label: 'Who We Help',
    href: '/personas',
    hasDropdown: true,
    isPersonaDropdown: true,
  },

  {
    label: 'Resources',
    href: '/resources',
    hasDropdown: true,
    highlightedLinks: [
      {
        label: 'News & Articles',
        href: '/articles',
        description: 'The latest immigration news and expert insights.',
        icon: FileText,
      },
      {
        label: 'Visa Guides',
        href: '/guides',
        description: 'In-depth guides to help you navigate the visa process.',
        icon: BookOpen,
      },
      {
        label: 'Videos',
        href: '/videos',
        description: 'Shorts and full-length videos on visas and immigration strategy.',
        icon: PlayCircle,
      },
      {
        label: 'Events',
        href: '/events',
        description: 'Seminars, webinars, and conferences we host and attend.',
        icon: CalendarDays,
      },
      {
        label: 'Speak to an Attorney',
        href: '/consultation',
        description: 'Book a consultation with our immigration team.',
        icon: MessageCircle,
      },
    ],
    dropdownSections: [
      {
        title: 'Visa Roadmaps',
        href: '/guides',
        icon: Map,
        links: [
          { label: 'H-1B Visa Roadmap', href: '/guides/h-1b-visa-roadmap' },
          { label: 'TN Visa Roadmap', href: '/guides/tn-visa-roadmap' },
          { label: 'O-1 Visa Roadmap', href: '/guides/o-1-visa-roadmap' },
          { label: 'L-1 Visa Roadmap', href: '/guides/l-1-visa-roadmap' },
          { label: 'E-2 Investor Roadmap', href: '/guides/e-2-visa-roadmap' },
        ],
      },
      {
        title: 'Tools',
        href: '/tools',
        icon: Calculator,
        links: [
          { label: 'Immigration Assessment', href: '/eligibility' },
          { label: 'H-1B Max-Out Calculator', href: '/h1b-maxout-calculator' },
        ],
      },
      {
        title: 'For Clients',
        href: '/for-clients',
        icon: CreditCard,
        links: [
          { label: 'Pay My Bill', href: '/pay-my-bill' },
          { label: 'Client Portal', href: '/client-portal' },
          { label: 'Book a Consultation', href: '/consultation' },
        ],
      },
    ],
  },
  {
    label: 'Visa Types',
    href: '/visas',
    hasDropdown: true,
    dropdownSections: [
      {
        title: 'Work Visas',
        href: '/visas?category=work',
        icon: Briefcase,
        links: [
          { label: 'H-1B Visa', href: '/visas/h-1b' },
          { label: 'L-1 Visa', href: '/visas/l-1' },
          { label: 'O-1 Visa', href: '/visas/o-1' },
          { label: 'E-3 Visa', href: '/visas/e-3' },
          { label: 'TN Visa', href: '/visas/tn' },
        ],
      },
      {
        title: 'Green Cards',
        href: '/visas?category=green-card',
        icon: Shield,
        links: [
          { label: 'EB-1 Green Card', href: '/visas/eb-1' },
          { label: 'EB-2 Green Card', href: '/visas/eb-2' },
          { label: 'National Interest Waiver (NIW)', href: '/visas/niw' },
          { label: 'PERM Labor Certification', href: '/visas/perm' },
        ],
      },
      {
        title: 'Investor Visas',
        href: '/visas?category=investor',
        icon: TrendingUp,
        links: [
          { label: 'E-2 Treaty Investor', href: '/visas/e-2' },
          { label: 'EB-5 Investor Visa', href: '/visas/eb-5' },
          { label: 'B-1 Visa Roadmap', href: '/guides/b-1-visa-roadmap' },
          { label: 'B-2 Visa Roadmap', href: '/guides/b-2-visa-roadmap' },
        ],
      },
      {
        title: 'Family & Status',
        href: '/visas?category=family',
        icon: Users,
        links: [
          { label: 'Family-Based Green Cards', href: '/families' },
          { label: 'Adjustment of Status', href: '/visas/adjustment-of-status' },
          { label: 'Consular Processing', href: '/visas/consular-processing' },
          { label: 'Naturalization', href: '/visas/naturalization' },
        ],
      },
      {
        title: 'Additional Services',
        href: '/visas?category=other',
        icon: Layers,
        links: [
          { label: 'I-140 Immigrant Petition', href: '/visas/i-140' },
          { label: 'Change or Extension of Status', href: '/visas/change-extension-of-status' },
        ],
      },
    ],
  },
  {
    label: 'Contact',
    href: '/contact',
  },
]

interface NavbarProps {
  transparent?: boolean
  variant?: 'default' | 'blue'
  featuredResource?: FeaturedResource | null
}

export function Navbar({
  transparent = false,
  variant = 'default',
  featuredResource,
}: NavbarProps) {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const [isScrolled, setIsScrolled] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState<string | null>(null)
  const [showBanner, setShowBanner] = useState(true)
  const [bannerRevealed, setBannerRevealed] = useState(true)
  const bannerContentRef = useRef<HTMLDivElement>(null)
  const lastScrollY = useRef(0)
  const [bannerHeight, setBannerHeight] = useState(0)

  const NAV_BAR_HEIGHT = 80
  const BANNER_TRANSITION_MS = 300

  useEffect(() => {
    if (localStorage.getItem('sip-rebrand-banner-dismissed') === '1') {
      setShowBanner(false)
    }
  }, [])

  // Measure banner height once (and on resize) — not on every collapse frame.
  useLayoutEffect(() => {
    const el = bannerContentRef.current
    if (!showBanner || !el) {
      setBannerHeight(0)
      return
    }

    const measure = () => setBannerHeight(el.offsetHeight)
    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [showBanner])

  const bannerOffset = showBanner && bannerRevealed ? bannerHeight : 0
  const navOverlayHeight = NAV_BAR_HEIGHT + bannerOffset
  const overlayTopStyle = { top: navOverlayHeight }
  const bannerMotionStyle = {
    maxHeight: bannerRevealed ? bannerHeight : 0,
    transition: `max-height ${BANNER_TRANSITION_MS}ms ease-in-out`,
  }
  const bannerSpacerStyle = {
    height: bannerOffset,
    transition: `height ${BANNER_TRANSITION_MS}ms ease-in-out`,
  }

  const dismissBanner = () => {
    setShowBanner(false)
    document.documentElement.classList.add('banner-dismissed')
    try {
      localStorage.setItem('sip-rebrand-banner-dismissed', '1')
    } catch {
      // ignore storage errors (private mode, etc.)
    }
  }

  useLayoutEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY

      if (transparent) {
        setIsScrolled(scrollY > 20)
      }

      if (showBanner) {
        const delta = scrollY - lastScrollY.current

        if (scrollY <= 12) {
          setBannerRevealed(true)
        } else if (delta > 10 && scrollY > 48) {
          setBannerRevealed(false)
        } else if (delta < -16) {
          setBannerRevealed(true)
        }
      }

      lastScrollY.current = scrollY
    }

    handleScroll()

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [transparent, showBanner])

  const shouldShowBackground = transparent
    ? isScrolled || activeDropdown !== null || mobileMenuOpen
    : true

  const isBlueVariant =
    variant === 'blue' && !isScrolled && activeDropdown === null && !mobileMenuOpen

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-[100] transition-[background-color,box-shadow,backdrop-filter] duration-300 ${
          shouldShowBackground ? 'bg-brand-blue backdrop-blur-xl shadow-lg' : 'bg-transparent'
        }`}
      >
        {/* Rebrand announcement — collapses on scroll down, re-expands on scroll up */}
        {showBanner && (
          <div className="overflow-hidden" style={bannerMotionStyle}>
            <div
              ref={bannerContentRef}
              className="rebrand-banner relative bg-brand-gold text-brand-blue"
            >
              <div className="px-4 md:px-12 py-1.5 md:py-3">
                <div className="max-w-380 mx-auto flex flex-nowrap items-center justify-center gap-x-1 md:gap-x-3 pr-7 md:pr-8 max-sm:scale-[0.88] max-sm:origin-center">
                  <p className="text-[10px] leading-none sm:text-xs md:text-sm font-medium whitespace-nowrap">
                    <span className="font-semibold">Serotte Law Firm</span> is becoming{' '}
                    <span className="font-semibold">Serotte Immigration Partners</span>.
                  </p>
                  <Link
                    href="/articles/a-new-name-for-the-firm-weve-been-building"
                    onClick={() =>
                      trackCtaClicked({
                        cta_text: 'Learn More',
                        cta_location: 'nav',
                        destination_url: '/articles/a-new-name-for-the-firm-weve-been-building',
                      })
                    }
                    className="group inline-flex shrink-0 items-center gap-0.5 text-[10px] sm:text-xs md:text-sm font-semibold underline underline-offset-2 hover:text-brand-blue/80 transition-colors whitespace-nowrap"
                  >
                    Learn More
                    <ArrowRight className="w-3 h-3 md:w-3.5 md:h-3.5 group-hover:translate-x-0.5 transition-transform" />
                  </Link>
                </div>
              </div>
              <button
                onClick={dismissBanner}
                aria-label="Dismiss announcement"
                className="absolute right-3 md:right-4 top-1/2 -translate-y-1/2 text-brand-blue/70 hover:text-brand-blue transition-colors"
              >
                <X className="w-3.5 h-3.5 md:w-4 md:h-4" />
              </button>
            </div>
          </div>
        )}

        <div className="px-6 md:px-12 py-2">
          <div className="max-w-380 mx-auto">
            <div className="flex items-center justify-between h-16">
              {/* Logo */}
              <Link href="/" className="flex items-center">
                <Image
                  src={
                    isBlueVariant
                      ? mediaFile('Serotte Word Mark_Navy-1.svg')
                      : mediaFile('Serotte Word Mark_Stone-1.svg')
                  }
                  alt="Serotte Law Firm"
                  width={120}
                  height={40}
                />
              </Link>

              {/* Navigation Links */}
              <div className="hidden lg:flex items-center gap-6">
                {navItems.map((item) => (
                  <div key={item.label} className="relative">
                    {item.hasDropdown ? (
                      <Link
                        href={item.href}
                        className={`flex items-center gap-1 transition-all text-sm font-medium px-3 py-2 rounded-md ${activeDropdown === item.label
                          ? isBlueVariant
                            ? 'text-brand-blue bg-brand-blue/10'
                            : 'text-white bg-white/10'
                          : isBlueVariant
                            ? 'text-brand-blue/90 hover:text-brand-blue hover:bg-brand-blue/5'
                            : 'text-white/90 hover:text-white hover:bg-white/5'
                          }`}
                        onMouseEnter={() => setActiveDropdown(item.label)}
                        onFocus={() => setActiveDropdown(item.label)}
                      >
                        {item.label}
                        <ChevronDown
                          className={`w-4 h-4 transition-transform ${activeDropdown === item.label ? 'rotate-180' : ''
                            }`}
                        />
                      </Link>
                    ) : (
                      <Link
                        href={item.href}
                        className={`transition-all text-sm font-medium px-3 py-2 rounded-md ${isBlueVariant
                          ? 'text-brand-blue/90 hover:text-brand-blue hover:bg-brand-blue/5'
                          : 'text-white/90 hover:text-white hover:bg-white/5'
                          }`}
                      >
                        {item.label}
                      </Link>
                    )}
                  </div>
                ))}
              </div>

              {/* Right Actions */}
              <div className="flex items-center gap-4">
                {/* <button
                  className={`hidden md:block transition-colors ${
                    isBlueVariant
                      ? 'text-brand-blue/90 hover:text-brand-blue'
                      : 'text-white/90 hover:text-white'
                  }`}
                >
                  <Search className="w-5 h-5" />
                </button> */}
                <Link
                  href="/consultation"
                  onClick={() =>
                    trackCtaClicked({
                      cta_text: 'Book a Consultation',
                      cta_location: 'nav',
                      destination_url: '/consultation',
                    })
                  }
                  className={`hidden md:flex px-5 py-2 font-semibold rounded-full transition-colors text-sm items-center gap-2 ${isBlueVariant
                    ? 'bg-brand-blue text-white hover:bg-brand-blue-muted'
                    : 'bg-white text-brand-blue hover:bg-white/90'
                    }`}
                >
                  Book a Consultation
                  <ArrowRight className="w-4 h-4" />
                </Link>

                {/* Mobile menu button */}
                <button
                  className={`lg:hidden p-2 ${isBlueVariant ? 'text-brand-blue' : 'text-white'}`}
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  aria-label="Toggle menu"
                >
                  {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Spacer to offset content by the banner height on pages whose content
          sits directly under the (fixed) nav. Transparent hero pages opt out. */}
      {!transparent && showBanner && bannerHeight > 0 && (
        <div aria-hidden style={bannerSpacerStyle} />
      )}

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className="fixed left-0 right-0 z-40 bg-white shadow-xl lg:hidden overflow-hidden"
            style={overlayTopStyle}
          >
            <div
              className="px-6 py-6 overflow-y-auto"
              style={{ maxHeight: `calc(100vh - ${navOverlayHeight}px)` }}
            >
              {navItems.map((item) => (
                <div key={item.label} className="border-b border-gray-100 last:border-0">
                  {item.hasDropdown ? (
                    <div>
                      <div className="flex items-center border-b border-transparent">
                        <Link
                          href={item.href}
                          className="flex-1 py-4 text-gray-900 font-medium hover:text-brand-blue transition-colors"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {item.label}
                        </Link>
                        <button
                          className="p-2 text-gray-900"
                          aria-label={`Toggle ${item.label} submenu`}
                          aria-expanded={mobileDropdownOpen === item.label}
                          onClick={() =>
                            setMobileDropdownOpen(
                              mobileDropdownOpen === item.label ? null : item.label,
                            )
                          }
                        >
                          <ChevronDown
                            className={`w-5 h-5 transition-transform ${mobileDropdownOpen === item.label ? 'rotate-180' : ''
                              }`}
                          />
                        </button>
                      </div>
                      <AnimatePresence>
                        {mobileDropdownOpen === item.label && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="pb-4 space-y-4">
                              {/* Persona dropdown in mobile */}
                              {item.isPersonaDropdown ? (
                                <div className="space-y-2">
                                  {personas.map((persona) => (
                                    <Link
                                      key={persona.slug}
                                      href={getPersonaHref(persona.slug)}
                                      className="flex items-center gap-3 py-3 px-4 hover:bg-gray-50 rounded-lg transition-colors"
                                      onClick={() => {
                                        setMobileMenuOpen(false)
                                        setMobileDropdownOpen(null)
                                      }}
                                    >
                                      <div className="p-2 rounded-lg bg-brand-blue/10 text-brand-blue">
                                        <persona.icon className="w-4 h-4" />
                                      </div>
                                      <div>
                                        <span className="block font-medium text-gray-900">
                                          {persona.name}
                                        </span>
                                        <span className="text-xs text-gray-600">
                                          {persona.visas}
                                        </span>
                                      </div>
                                    </Link>
                                  ))}
                                  <div className="pt-3 px-4 border-t border-gray-100 space-y-2">
                                    <Link
                                      href="/personas"
                                      className="block text-sm text-brand-blue font-medium"
                                      onClick={() => {
                                        setMobileMenuOpen(false)
                                        setMobileDropdownOpen(null)
                                      }}
                                    >
                                      View all who we help →
                                    </Link>
                                    <Link
                                      href="/eligibility"
                                      className="block text-sm text-gray-600 font-medium"
                                      onClick={() => {
                                        setMobileMenuOpen(false)
                                        setMobileDropdownOpen(null)
                                      }}
                                    >
                                      Not sure? Start your immigration assessment →
                                    </Link>
                                  </div>
                                </div>
                              ) : (
                                /* Standard dropdown sections */
                                <>
                                  {/* Highlighted Links (Mobile) */}
                                  {item.highlightedLinks && (
                                    <div className="space-y-2 mb-4 pb-4 border-b border-gray-100">
                                      {item.highlightedLinks.map((hl) => (
                                        <Link
                                          key={hl.href}
                                          href={hl.href}
                                          className="flex items-center gap-3 py-3 px-4 hover:bg-gray-50 rounded-lg transition-colors"
                                          onClick={() => {
                                            setMobileMenuOpen(false)
                                            setMobileDropdownOpen(null)
                                          }}
                                        >
                                          <div className="p-2 rounded-lg bg-brand-blue/10 text-brand-blue">
                                            <hl.icon className="w-4 h-4" />
                                          </div>
                                          <div>
                                            <span className="block font-medium text-gray-900">
                                              {hl.label}
                                            </span>
                                            <span className="text-xs text-gray-600">
                                              {hl.description}
                                            </span>
                                          </div>
                                        </Link>
                                      ))}
                                    </div>
                                  )}
                                  {item.dropdownSections?.map((section) => (
                                    <div key={section.title}>
                                      {'href' in section && section.href ? (
                                        <Link
                                          href={section.href}
                                          className="text-sm font-bold text-gray-900 mb-2 px-4 flex items-center gap-2 hover:text-brand-blue transition-colors"
                                          onClick={() => {
                                            setMobileMenuOpen(false)
                                            setMobileDropdownOpen(null)
                                          }}
                                        >
                                          {section.icon && (
                                            <section.icon className="w-4 h-4 text-brand-blue" />
                                          )}
                                          {section.title}
                                        </Link>
                                      ) : (
                                        <h4 className="text-sm font-bold text-gray-900 mb-2 px-4 flex items-center gap-2">
                                          {section.icon && (
                                            <section.icon className="w-4 h-4 text-brand-blue" />
                                          )}
                                          {section.title}
                                        </h4>
                                      )}
                                      <ul className="space-y-2">
                                        {section.links.map((link) => (
                                          <li key={link.href}>
                                            <Link
                                              href={link.href}
                                              className="block py-2 px-4 text-gray-600 hover:bg-background hover:text-brand-blue transition-colors"
                                              onClick={() => {
                                                setMobileMenuOpen(false)
                                                setMobileDropdownOpen(null)
                                              }}
                                            >
                                              {link.label}
                                            </Link>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  ))}

                                  {/* Featured Resource - Mobile (Resources dropdown only) */}
                                  {item.label === 'Resources' && featuredResource && (
                                    <div className="mt-4 pt-4 border-t border-gray-100 px-4">
                                      <Link
                                        href={featuredResource.href}
                                        className="group block bg-gradient-to-br from-brand-blue/5 to-brand-blue/10 rounded-xl p-4"
                                        onClick={() => {
                                          setMobileMenuOpen(false)
                                          setMobileDropdownOpen(null)
                                        }}
                                      >
                                        <div className="flex gap-4">
                                          {/* Thumbnail */}
                                          <div className="relative w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-brand-blue/10">
                                            {getMediaUrl(featuredResource.image, 'card') ? (
                                              <Image
                                                src={getMediaUrl(featuredResource.image, 'card')}
                                                alt={featuredResource.title}
                                                fill
                                                className="object-cover"
                                              />
                                            ) : (
                                              <div className="absolute inset-0 flex items-center justify-center">
                                                {featuredResource.type === 'guide' ? (
                                                  <BookOpen className="w-6 h-6 text-brand-blue/30" />
                                                ) : (
                                                  <FileText className="w-6 h-6 text-brand-blue/30" />
                                                )}
                                              </div>
                                            )}
                                          </div>
                                          {/* Content */}
                                          <div className="flex-1 min-w-0">
                                            <span className="inline-block px-2 py-0.5 bg-brand-blue text-white text-xs font-semibold rounded-full mb-1.5">
                                              {featuredResource.badge}
                                            </span>
                                            <h5 className="font-semibold text-gray-900 text-sm line-clamp-2 group-hover:text-brand-blue transition-colors">
                                              {featuredResource.title}
                                            </h5>
                                            <span className="text-xs text-brand-blue font-medium mt-1 inline-flex items-center gap-1">
                                              Read more
                                              <ArrowRight className="w-3 h-3" />
                                            </span>
                                          </div>
                                        </div>
                                      </Link>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      href={item.href}
                      className="block py-4 text-gray-900 font-medium hover:text-brand-blue transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}

              {/* Mobile CTA */}
              <div className="pt-6 space-y-3">
                <Link
                  href="/consultation"
                  className="w-full px-5 py-3 bg-brand-blue text-white font-semibold rounded-full hover:bg-brand-blue-muted transition-colors text-sm flex items-center justify-center gap-2"
                  onClick={() => {
                    trackCtaClicked({
                      cta_text: 'Book a Consultation',
                      cta_location: 'nav',
                      destination_url: '/consultation',
                    })
                    setMobileMenuOpen(false)
                  }}
                >
                  Book a Consultation
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop Floating Dropdown Menu */}
      <AnimatePresence>
        {activeDropdown && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            className="hidden lg:block fixed left-0 right-0 z-40 bg-white shadow-xl border-b border-gray-200"
            style={overlayTopStyle}
            onMouseLeave={() => setActiveDropdown(null)}
          >
            <div className="max-w-380 2xl:max-w-360 mx-auto px-12 md:px-6 py-10">
              {/* Persona Dropdown - Special Layout */}
              {navItems.find((item) => item.label === activeDropdown)?.isPersonaDropdown ? (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {personas.map((persona, idx) => (
                      <motion.div
                        key={persona.slug}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: idx * 0.03, ease: [0.4, 0, 0.2, 1] }}
                      >
                        <Link
                          href={getPersonaHref(persona.slug)}
                          className="group block p-4 rounded-xl hover:bg-gray-50 transition-all border border-transparent hover:border-gray-200"
                          onClick={() => setActiveDropdown(null)}
                        >
                          <div className="flex items-start gap-3">
                            <div className="p-2 rounded-lg bg-brand-blue/10 text-brand-blue group-hover:bg-brand-blue group-hover:text-white transition-colors">
                              <persona.icon className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-gray-900 group-hover:text-brand-blue transition-colors">
                                {persona.name}
                              </h4>
                              <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">
                                {persona.description}
                              </p>
                              <p className="text-xs text-brand-blue/70 mt-2 font-medium">
                                {persona.visas}
                              </p>
                            </div>
                          </div>
                        </Link>
                      </motion.div>
                    ))}
                  </div>

                  {/* Bottom Action for Personas */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.15 }}
                    className="mt-6 pt-6 border-t border-gray-200 flex items-center justify-between gap-4"
                  >
                    <p className="text-gray-600 text-sm">
                      Not sure where you fit?{' '}
                      <Link
                        href="/eligibility"
                        className="text-brand-blue font-medium hover:underline"
                        onClick={() => setActiveDropdown(null)}
                      >
                        Start your immigration assessment
                      </Link>
                    </p>
                    <Link
                      href="/personas"
                      className="text-brand-blue font-medium flex items-center gap-2 hover:gap-3 transition-all shrink-0"
                      onClick={() => setActiveDropdown(null)}
                    >
                      View all who we help
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </motion.div>
                </>
              ) : (
                /* Standard Dropdown - with optional featured resource for Resources */
                <>
                  <div
                    className={`grid gap-10 ${activeDropdown === 'Resources' && featuredResource
                      ? 'grid-cols-[1.1fr_1fr_0.85fr_0.85fr_1.1fr]'
                      : activeDropdown === 'Resources'
                        ? 'grid-cols-4'
                        : 'grid-cols-5'
                      }`}
                  >
                    {/* Highlighted Links as left column - Resources only */}
                    {activeDropdown === 'Resources' &&
                      navItems.find((item) => item.label === activeDropdown)?.highlightedLinks && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                          className="flex flex-col gap-2 -mt-2.5"
                        >
                          {navItems
                            .find((item) => item.label === activeDropdown)
                            ?.highlightedLinks?.map((hl) => (
                              <Link
                                key={hl.href}
                                href={hl.href}
                                className="group flex items-start gap-3 px-4 py-3 rounded-lg hover:bg-brand-blue/5 transition-all"
                                onClick={() => setActiveDropdown(null)}
                              >
                                <div className="p-1.5 rounded-md bg-brand-blue/10 text-brand-blue group-hover:bg-brand-blue group-hover:text-white transition-colors shrink-0">
                                  <hl.icon className="w-4 h-4" />
                                </div>
                                <div>
                                  <span className="font-medium text-sm text-gray-900 group-hover:text-brand-blue transition-colors block">
                                    {hl.label}
                                  </span>
                                  <span className="text-xs text-gray-600 block leading-relaxed">
                                    {hl.description}
                                  </span>
                                </div>
                              </Link>
                            ))}
                        </motion.div>
                      )}

                    {navItems
                      .find((item) => item.label === activeDropdown)
                      ?.dropdownSections?.map((section, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: idx * 0.05, ease: [0.4, 0, 0.2, 1] }}
                        >
                          {'href' in section && section.href ? (
                            <Link
                              href={section.href}
                              className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2 hover:text-brand-blue transition-colors group"
                              onClick={() => setActiveDropdown(null)}
                            >
                              {section.icon && (
                                <section.icon className="w-5 h-5 text-brand-blue" />
                              )}
                              <span className="inline-flex items-center gap-1.5">
                                {section.title}
                                <ArrowRight className="w-4 h-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                              </span>
                            </Link>
                          ) : (
                            <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                              {section.icon && <section.icon className="w-5 h-5 text-brand-blue" />}
                              {section.title}
                            </h3>
                          )}
                          <ul className="space-y-4">
                            {section.links.map((link) => (
                              <li key={link.href}>
                                <Link
                                  href={link.href}
                                  className="text-gray-600 hover:text-brand-blue transition-colors block"
                                  onClick={() => setActiveDropdown(null)}
                                >
                                  {link.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </motion.div>
                      ))}

                    {/* Featured Resource Column - Only for Resources dropdown */}
                    {activeDropdown === 'Resources' && featuredResource && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}
                        className="pl-8 border-l border-gray-200"
                      >
                        <Link
                          href={featuredResource.href}
                          className="group block"
                          onClick={() => setActiveDropdown(null)}
                        >
                          {/* Featured Image */}
                          <div className="relative aspect-[16/9] rounded-xl overflow-hidden mb-4 bg-gradient-to-br from-brand-blue/10 to-brand-blue/5">
                            {getMediaUrl(featuredResource.image, 'card') ? (
                              <Image
                                src={getMediaUrl(featuredResource.image, 'card')}
                                alt={featuredResource.title}
                                fill
                                className="object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                            ) : (
                              <div className="absolute inset-0 flex items-center justify-center">
                                {featuredResource.type === 'guide' ? (
                                  <BookOpen className="w-12 h-12 text-brand-blue/30" />
                                ) : (
                                  <FileText className="w-12 h-12 text-brand-blue/30" />
                                )}
                              </div>
                            )}
                            {/* Badge overlay */}
                            <div className="absolute top-3 left-3">
                              <span className="px-2.5 py-1 bg-brand-blue text-white text-xs font-semibold rounded-full">
                                {featuredResource.badge}
                              </span>
                            </div>
                          </div>

                          {/* Content */}
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-xs text-gray-600 font-medium uppercase tracking-wide">
                              {featuredResource.type === 'guide' ? (
                                <>
                                  <BookOpen className="w-3.5 h-3.5" />
                                  Visa Guide
                                </>
                              ) : (
                                <>
                                  <FileText className="w-3.5 h-3.5" />
                                  Article
                                </>
                              )}
                            </div>
                            <h4 className="font-semibold text-gray-900 group-hover:text-brand-blue transition-colors line-clamp-2">
                              {featuredResource.title}
                            </h4>
                            {featuredResource.description && (
                              <p className="text-sm text-gray-600 line-clamp-2">
                                {featuredResource.description}
                              </p>
                            )}
                            <span className="inline-flex items-center gap-1.5 text-sm text-brand-blue font-medium group-hover:gap-2.5 transition-all pt-1">
                              Read more
                              <ArrowRight className="w-4 h-4" />
                            </span>
                          </div>
                        </Link>
                      </motion.div>
                    )}
                  </div>

                  {/* Bottom Action - Visa Types only */}
                  {activeDropdown === 'Visa Types' && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3, delay: 0.15 }}
                      className="mt-8 pt-6 border-t border-gray-200 flex items-center justify-between"
                    >
                      <p className="text-gray-600 text-sm">
                        Don&apos;t see what you&apos;re looking for?{' '}
                        <Link
                          href="/consultation"
                          className="text-brand-blue font-medium hover:underline"
                          onClick={() => {
                            trackCtaClicked({
                              cta_text: 'We can still help',
                              cta_location: 'nav',
                              destination_url: '/consultation',
                            })
                            setActiveDropdown(null)
                          }}
                        >
                          We can still help
                        </Link>
                        <span className="text-gray-500">
                          {' '}
                          — we&apos;re a full-service immigration firm.
                        </span>
                      </p>
                      <Link
                        href="/visas"
                        className="text-brand-blue font-medium flex items-center gap-2 hover:gap-3 transition-all ml-auto"
                        onClick={() => setActiveDropdown(null)}
                      >
                        View all visa types
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </motion.div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
